import {
  ALERT_BANNER_ERROR_TYPE,
  ALERT_BANNER_SUCCESS_TYPE,
  ALERT_BANNER_SUPPORTED_TYPES,
} from '../../../../utils/spds/constants/feedback';
import { Arg, CONTENT_ARGS, Metadata, pick } from '../../../../utils/spds/metadata';
import { COLUMN, FLEX_START, LAYOUT_TYPES } from '../../../../utils/spds/constants/css-layout';
import { Dismissible } from '../../core/capability/dismissible.gjs';
import { compose } from '../../../../utils/spds/capabilities/helpers';
import { or } from 'ember-truth-helpers';
import Body from './message/body.gjs';
import CTA from './message/cta.gjs';
import CloseButton from '../../action/button/close.gjs';
import FlexColumn from '../../layout/flex/column.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import Super from '../../core/base.gjs';
import Variable from '../../layout/variable.gjs';
import styles from './message.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const METADATA = Metadata.define({
  description: `
Banners appear at the top of the screen or content section to display critical messaging about the loss of data, functionality, or important site-wide information that affects the user's ability to use the product.

### Implementation options
Alert banners can be implemented in three ways:
  * Added to a page using the \`Spds::Feedback::AlertBanner::Message\` component
  * Programmatically generated with the \`Spds::Feedback::AlertBanner::Container\` component
  * Globally by using the \`alertManager\` service to display a message at the application route level

### alertManager Service
The \`alertManager\` service allows for control of the global banner variant. See the example invocation below for detail
  * The service can be used to display a global banner by passing required data into its \`broadcast\` function
  * The service exposes a \`clear\` function to allow global banners to be cleared from the page (\`alertManager.clear('banner')\`)

### \`Spds::Feedback::AlertBanner::Container\` component
This component allows for programmatically generating an alert message using the \`alertManager\` service
  * Accepts the same payload as the global banner
  * Including the \`containerId\` value allows rendering to the matching alert container on the page
  * Exposes a \`clear\` function that will remove the first alert within the container (\`alertManager.clear('banner', containerId)\`)

### Named blocks
Alert banners optionally support three named blocks when embedded in a page:
  * \`:header\`
  * \`:body\`
  * \`:cta-container\`

### Dismissal
The open/close lifecycle comes from the \`Dismissible\` capability. \`@onBeforeClose\` can block a dismissal, and repeat requests
are dropped rather than queued so \`@onClose\` fires once per dismissal — which matters for the container variant, since it
removes the alert from its list on that hook.
  `,
  extends: [
    pick(CONTENT_ARGS, [
      {
        name: 'icon',
        description:
          'Custom icon name; when not provided, a default icon is used based on type if includeIcon is true',
      },
    ]),
  ],
  meta: {
    body: Arg.string({ description: 'Body text content for the banner' }).initial('Example body'),
    bodyColumn: Arg.boolean({ default: true, description: 'Arrange body text in a column layout' }),
    bodyTag: Arg.string({
      default: 'div',
      description: 'HTML tag to use for the body text element',
    }),
    closeable: Arg.boolean({
      description: 'Whether the banner can be dismissed via close button (global context)',
    }),
    ctaAlignment: Arg.oneOf(LAYOUT_TYPES, { description: 'Layout direction for CTA buttons' }),
    ctaGap: Arg.string({ default: '16px', description: 'Gap between CTA buttons' }),
    global: Arg.boolean({
      description: 'Indicates the banner is in a global (page-level) context',
    }),
    header: Arg.string({ description: 'Header text displayed above the body' }).initial(
      'Example header'
    ),
    includeIcon: Arg.boolean({
      description: 'Display a type-based icon alongside the message',
      initialValue: true,
    }),
    messageAlignItems: Arg.string({
      default: FLEX_START,
      description: 'CSS align-items for the message container',
    }),
    messageFlex: Arg.string({
      default: '1',
      description: 'CSS flex value for the message container',
    }),
    messageGap: Arg.string({
      default: '12px',
      description: 'Gap between message elements',
    }),
    onPrimaryBtnClick: Arg.action({ description: 'Action for the primary CTA button' }),
    onSecondaryBtnClick: Arg.action({ description: 'Action for the secondary CTA button' }),
    primaryBtnText: Arg.string({ description: 'Text label for the primary CTA button' }).initial(
      'Primary CTA'
    ),
    secondaryBtnText: Arg.string({
      description: 'Text label for the secondary CTA button',
    }).initial('Secondary CTA'),
    type: Arg.oneOf(ALERT_BANNER_SUPPORTED_TYPES, {
      description: 'Visual style variant of the banner',
      initialValue: 'error',
    }).required(),
  },
  name: 'Alert Banner Message',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=24273-64556&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/42e14e-alert-banner',
  },
});

export default class SpdsFeedbackAlertBannerMessage extends compose(Super, Dismissible) {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-alert-banner-message-container';
  styles = styles;

  get bodyColumn() {
    return this.argOrDefault('bodyColumn');
  }

  get bodyTextTag() {
    return this.argOrDefault('bodyTag');
  }

  get classesToMap() {
    return [...super.classesToMap, 'global', 'open', 'type'];
  }

  get ctaAlignment() {
    return this.argOrDefault('ctaAlignment') === COLUMN;
  }

  get ctaGap() {
    return this.argOrDefault('ctaGap');
  }

  get hasActions() {
    let { onPrimaryBtnClick, onSecondaryBtnClick, primaryBtnText, secondaryBtnText } = this.args;

    return Boolean(onPrimaryBtnClick || onSecondaryBtnClick || primaryBtnText || secondaryBtnText);
  }

  get icon() {
    let { icon, includeIcon, type } = this.args;

    if (icon) return icon;

    if (includeIcon) {
      switch (type) {
        case ALERT_BANNER_ERROR_TYPE:
          return 'triangle-exclamation-mark';
        case ALERT_BANNER_SUCCESS_TYPE:
          return 'circle-check';
        default:
          return 'circle-i';
      }
    }

    return null;
  }

  get messageGap() {
    return this.argOrDefault('messageGap');
  }

  get messageAlignItems() {
    return this.argOrDefault('messageAlignItems');
  }

  get messageFlex() {
    return this.argOrDefault('messageFlex');
  }

  get open() {
    return this.isVisible;
  }

  get showCloseBtn() {
    let { closeable, global, onClose } = this.args;

    return (closeable && global) || onClose;
  }

  /**
   * The banner renders plain markup, so there is no element to dismiss. This only settles the
   * closing waiter and fires `@onClose` — the two things the modal gets for free from its dialog's
   * native `close` event.
   */
  dismissElement() {
    this.settleToken();
    this.reportClose();
  }

  <template>
    <FlexRow
      @fitContentHeight={{true}}
      @flex="1"
      class={{this.classes}}
      data-test-spds-feedback-alert-banner-message-container
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      <FlexRow
        @alignItems={{this.messageAlignItems}}
        @fitContentHeight={{true}}
        @flex={{this.messageFlex}}
        @gap={{this.messageGap}}
        class="spds-feedback-alert-banner-message"
        data-test-spds-feedback-alert-banner-message
      >
        {{#if this.icon}}
          {{svgJar
            this.icon
            class="spds-feedback-alert-banner-icon"
            data-test-spds-feedback-alert-banner-icon=""
          }}
        {{/if}}
        <Variable
          @column={{this.ctaAlignment}}
          class="spds-feedback-alert-banner-body"
          data-test-spds-feedback-alert-banner-body
        >
          <FlexColumn
            class="spds-feedback-alert-banner-text-container"
            data-test-spds-feedback-alert-banner-text-container
          >
            {{#if (or (has-block "header") @header)}}
              <FlexRow
                @content={{@header}}
                @tag="h5"
                class="spds-feedback-alert-banner-header"
                data-test-spds-feedback-alert-banner-header
              >
                {{yield to="header"}}
              </FlexRow>
            {{/if}}
            <Variable
              @column={{this.bodyColumn}}
              @tag={{this.bodyTextTag}}
              class="spds-feedback-alert-banner-body-text"
              data-test-spds-feedback-alert-banner-body-text
            >
              {{#if (has-block "body")}}
                {{yield to="body"}}
              {{else}}
                <Body @body={{@body}} />
              {{/if}}
            </Variable>
          </FlexColumn>
          {{#if (or this.hasActions (has-block "cta-container"))}}
            <FlexRow
              @gap={{this.ctaGap}}
              class="spds-feedback-alert-banner-cta-container"
              data-test-spds-feedback-alert-banner-cta-container
            >
              {{#if (has-block "cta-container")}}
                {{yield to="cta-container"}}
              {{else}}
                <CTA
                  @onPrimaryBtnClick={{@onPrimaryBtnClick}}
                  @onSecondaryBtnClick={{@onSecondaryBtnClick}}
                  @primaryBtnText={{@primaryBtnText}}
                  @secondaryBtnText={{@secondaryBtnText}}
                />
              {{/if}}
            </FlexRow>
          {{/if}}
        </Variable>
        {{#if this.showCloseBtn}}
          <CloseButton
            @close={{this.beforeClose}}
            class="spds-feedback-alert-banner-close"
            data-test-spds-feedback-alert-banner-close-button
          />
        {{/if}}
      </FlexRow>
    </FlexRow>
  </template>
}

export const ALERT_BANNER_MESSAGE_METADATA = SpdsFeedbackAlertBannerMessage.metadata;
